import React, { useState, Fragment, useEffect } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { autoUpdate, flip, useFloating } from '@floating-ui/react-dom';
import classNames from 'classnames';
import { DefaultStackIcon } from '../SingleStackTag';
import { icons } from '../../../../helpers/icons';
import { getStackIconByName } from '../../../../helpers/stackIcons';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import {
  filterCourses,
  setActiveFilterType,
} from '../../../../store/reducers/librarySlice';
import { TNamedItem } from '../../../../types';

interface SelectStackTagProps {
  stack: TNamedItem;
  className?: string;
}

const SelectStackTag: React.FC<SelectStackTagProps> = ({
  stack,
  className = '',
}) => {
  return (
    <div
      className={classNames(
        'flex items-start gap-2 p-1 rounded-[4px]',
        className,
      )}
    >
      {getStackIconByName(stack.name) ? (
        <img
          src={getStackIconByName(stack.name)}
          alt={`${stack.name} icon`}
          className="w-6 h-6 rounded-[4px]"
        />
      ) : (
        <DefaultStackIcon />
      )}
      <div className="text-regular text-grey-700">{stack.name}</div>
    </div>
  );
};

interface IStackTagsSelectProps {
  stacks: TNamedItem[];
  onSelectStack?: (stack: TNamedItem) => void;
  className?: string;
}

const StackTagsSelect: React.FC<IStackTagsSelectProps> = ({
  stacks,
  className = '',
}) => {
  const dispatch = useAppDispatch();
  const { activeFilters, activeFilterType } = useAppSelector(
    (state) => state.libraryReducer,
  );

  // flip the listbox menu if there is not enough space
  const { refs, floatingStyles } = useFloating({
    middleware: [flip()],
    whileElementsMounted: autoUpdate,
  });

  // Find the active stack from the active filters if it exists and is in the stacks array
  const findActiveStack = (): TNamedItem | undefined => {
    if (activeFilterType === 'stacks' && activeFilters.secondaryFilter) {
      const activeStack = stacks.find(
        (stack) =>
          stack.id === activeFilters.secondaryFilter?.id ||
          stack.name === activeFilters.secondaryFilter?.name,
      );
      if (activeStack) {
        return activeStack;
      }
    }
    return undefined;
  };

  const [selectedStack, setSelectedStack] = useState<TNamedItem>(
    findActiveStack() || stacks[0],
  );

  // Update selected stack when active filter changes
  useEffect(() => {
    const activeStack = findActiveStack();
    if (activeStack) {
      setSelectedStack(activeStack);
    }
  }, [activeFilters, activeFilterType, stacks]);

  const handleChange = (stack: TNamedItem) => {
    setSelectedStack(stack);

    dispatch(setActiveFilterType('stacks'));
    dispatch(filterCourses({ secondaryFilter: stack }));
  };

  if (!stacks || stacks.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      {stacks.length === 1 ? (
        <div
          className={classNames(
            'flex items-center gap-2 cursor-pointer px-1 py-1 rounded-lg bg-grey-50 hover:bg-grey-100 h-11',
            className,
          )}
          onClick={() => handleChange(stacks[0])}
        >
          <div className="flex items-center min-w-0 gap-2">
            <div className="flex-shrink-0">
              {getStackIconByName(stacks[0].name) ? (
                <img
                  src={getStackIconByName(stacks[0].name)}
                  alt={`${stacks[0].name} icon`}
                  className="w-6 h-6 rounded-[4px]"
                />
              ) : (
                <DefaultStackIcon />
              )}
            </div>
            <div className="font-medium text-left text-grey-700">
              <span>{stacks[0].name}</span>
            </div>
          </div>
        </div>
      ) : (
        <Listbox value={selectedStack} onChange={handleChange}>
          <div className="relative">
            <Listbox.Button
              ref={refs.setReference}
              className="flex items-center justify-between w-full px-1 py-1 transition-colors rounded-lg cursor-pointer bg-grey-50 hover:bg-grey-100 h-11"
            >
              <div className="flex items-center min-w-0 gap-2">
                <div className="flex-shrink-0">
                  {getStackIconByName(selectedStack.name) ? (
                    <img
                      src={getStackIconByName(selectedStack.name)}
                      alt={`${selectedStack.name} icon`}
                      className="w-6 h-6 rounded-[4px]"
                    />
                  ) : (
                    <DefaultStackIcon />
                  )}
                </div>
                <div className="font-medium text-left truncate text-grey-700">
                  <span className="truncate">{selectedStack.name}</span>
                </div>
                {stacks.length > 1 && (
                  <span className="text-grey-700">{` +${
                    stacks.length - 1
                  }`}</span>
                )}
              </div>
              <img
                src={icons.arrowDown}
                alt="Arrow down"
                aria-hidden="true"
                className="flex-shrink-0 w-4 h-4 ml-2 transition-transform duration-300 ui-open:rotate-180"
              />
            </Listbox.Button>

            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options
                ref={refs.setFloating}
                style={floatingStyles}
                className="z-10 w-full overflow-auto bg-white rounded-b-lg shadow-md max-h-60"
              >
                <div className="flex flex-col w-full gap-2 p-2">
                  {stacks.map((stack) => {
                    return (
                      <Listbox.Option
                        key={stack.id}
                        value={stack}
                        className="cursor-pointer"
                      >
                        {({ active, selected }) => {
                          return (
                            <SelectStackTag
                              stack={stack}
                              className={classNames({
                                'bg-grey-50': active,
                                'bg-grey-100':
                                  selected ||
                                  selectedStack.name === stack.name ||
                                  (activeFilterType === 'stacks' &&
                                    activeFilters.secondaryFilter &&
                                    (activeFilters.secondaryFilter.id ===
                                      stack.id ||
                                      activeFilters.secondaryFilter.name ===
                                        stack.name)),
                              })}
                            />
                          );
                        }}
                      </Listbox.Option>
                    );
                  })}
                </div>
              </Listbox.Options>
            </Transition>
          </div>
        </Listbox>
      )}
    </div>
  );
};

export default StackTagsSelect;
